import { useEffect, useMemo, useRef } from 'react';
import { createServer } from 'node:http';
import type { AddressInfo } from 'node:net';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { useAsync } from '@wix/cli-ui-kit';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useHttpClient } from '@wix/cli-http-client-react';
import {
  attachLogger,
  deattachLogger,
} from '@wix/ambassador-ctp-app-cloud-backend-service-v1-app-cloud-backend-deployment/http';
import { useTunnel } from '@wix/cli-tunnel';
import { useDevCenterClient, ComponentType } from '@wix/dev-center-client';
import type { TopologyComponent } from '@wix/dev-center-client';
import { isType } from 'variant';
import type { OnLog } from './logsReqHandler.js';
import { LOGS_ENDPOINT, logsReqHandler } from './logsReqHandler.js';

const deploymentIdRegex = /([0-9a-f-]{36})/;

export const useLogsServer = ({
  onLog,
  version,
  appId,
}: {
  onLog: OnLog;
  version: string;
  appId: string;
}) => {
  const { reportError } = useErrorReporter();
  const { getAppByVersion } = useDevCenterClient();
  const createTunnelClient = useTunnel();
  const httpClient = useHttpClient({ type: 'backoffice' });
  const localServerPortResolverRef = useRef<(port: number) => void>();

  const server = useMemo(() => {
    return createServer((req, res) => {
      logsReqHandler(req, res)
        .onLog(onLog)
        .onError((code, err, info) => {
          server.close();
          throw new CliError({ code, cause: err, info });
        });
    });
  }, [onLog]);

  const localServerPortPromise = useMemo(() => {
    return new Promise<number>((resolve) => {
      localServerPortResolverRef.current = resolve;
    });
  }, []);

  useEffect(() => {
    server.listen(0, async () => {
      const port = (server.address() as AddressInfo).port;
      localServerPortResolverRef.current?.(port);
    });
    return () => {
      if (server.listening) {
        server.close();
      }
    };
  }, [server]);

  const { status } = useAsync(async () => {
    const localServerPort = await localServerPortPromise;
    const { url: tunnelUrl } = await createTunnelClient(localServerPort);
    const { data } = await getAppByVersion({ appId, version });

    const topologyComponent = data.components.find(
      (component): component is TopologyComponent => {
        return component.compType === ComponentType.TOPOLOGY;
      }
    );
    const match = deploymentIdRegex.exec(
      topologyComponent?.compData.topology.topology.backendApiUrl ?? ''
    );

    if (!match?.[1]) {
      throw new CliError({
        code: CliErrorCode.FailedToGetLogsTopology(),
        cause: null,
      });
    }
    const deploymentId = match[1];

    const loggerReq = {
      appId,
      deploymentId,
      destinationUrl: new URL(LOGS_ENDPOINT, tunnelUrl).href,
    };

    try {
      await httpClient.request(attachLogger(loggerReq));
    } catch (error) {
      server.close();
      throw new CliError({
        code: CliErrorCode.FailedToConnectToLogsService(),
        cause: error,
      });
    }

    return deploymentId;
  }, []);

  useEffect(() => {
    // Logs should be deattached only on unmount.
    // This is why this `useEffect` has only `return` statement.
    return () => {
      if (isType(status, 'Success')) {
        httpClient
          .request(
            deattachLogger({
              appId,
              deploymentId: status.result,
            })
          )
          .catch(reportError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return status;
};
